import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { useAppDispatch } from '../store/hooks';
import { useSelector } from 'react-redux';
import { STORAGE_KEYS } from '../config/constants';
import { RootState } from '../store/store';
import {
  fetchWishlist,
  addToWishlistAsync,
  removeFromWishlistAsync,
  clearWishlistState,
} from '../store/slices/wishlistSlice';
import { Product } from '../types';
import type { GroupPriceInfo } from '../types/customerGroups';

export interface WishlistProduct {
  id: number;
  slug: string;
  name: string;
  base_price: number | string;
  gross_price: number | string;
  image?: string;
  category?: string;
  in_stock?: boolean;
  /** Van aktív variánsa (méret/szín) → kosárba csak a termékoldalon, választás után. */
  has_variants?: boolean;
  addedAt?: string;
  // A kártya-árhoz (cardPrices): akció és — csak bejelentkezve, élő szerver-adatból — csoportár.
  current_gross_price?: number | string | null;
  is_on_sale?: boolean | null;
  sale_gross_price?: number | string | null;
  discount_percentage?: number | string | null;
  /** Néző-függő: a vendég-listában (localStorage) soha nem tárolódik. */
  group_price?: GroupPriceInfo | null;
}

/** A vendég-lista tétele a néző-függő csoportár nélkül (az csak élő szerver-adatból jöhet). */
const withoutGroupPrice = ({ group_price: _groupPrice, ...rest }: WishlistProduct): WishlistProduct => rest;

// --- Vendég-kívánságlista (localStorage) reaktív forrásként -------------------
// A `wishlistUpdated` (ugyanebben a fülben) és a `storage` (másik fülben)
// eseményre minden feliratkozott komponens újrarenderel.
const WISHLIST_EVENT = 'wishlistUpdated';

const subscribeGuestWishlist = (onChange: () => void) => {
  window.addEventListener(WISHLIST_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(WISHLIST_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
};

// A nyers stringet adjuk vissza: primitív, így a snapshot-összehasonlítás stabil.
const getGuestWishlistRaw = (): string => {
  try {
    return localStorage.getItem(STORAGE_KEYS.WISHLIST) ?? '';
  } catch {
    return '';
  }
};

const parseGuestWishlist = (raw: string): WishlistProduct[] => {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(withoutGroupPrice) : [];
  } catch {
    return [];
  }
};

const readGuestWishlist = () => parseGuestWishlist(getGuestWishlistRaw());

const writeGuestWishlist = (items: WishlistProduct[]) => {
  if (items.length === 0) {
    localStorage.removeItem(STORAGE_KEYS.WISHLIST);
  } else {
    localStorage.setItem(STORAGE_KEYS.WISHLIST, JSON.stringify(items.map(withoutGroupPrice)));
  }
  window.dispatchEvent(new CustomEvent(WISHLIST_EVENT));
};

const toWishlistProduct = (p: Product): WishlistProduct => ({
  id: p.id,
  slug: p.slug,
  name: p.name,
  base_price: p.base_price,
  gross_price: p.gross_price,
  image: p.main_image || p.images?.[0]?.image || '',
  category: typeof p.category === 'object' ? p.category?.name : p.category,
  // "in_stock" a kívánságlistán = rendelhető. Régi bejegyzéseken nincs
  // is_orderable, ott a polckészletre esünk vissza.
  in_stock: p.is_orderable ?? p.stock_quantity > 0,
  has_variants: (p.variants ?? []).some((v) => v.is_active !== false),
  current_gross_price: p.current_gross_price,
  is_on_sale: p.is_on_sale,
  sale_gross_price: p.sale_gross_price,
  discount_percentage: p.discount_percentage,
  group_price: p.group_price ?? null,
});

// A folyamatban lévő vendég-feltöltés. Ha az effect közben újrafut (StrictMode,
// gyors újrarenderelés), a második futás ugyanezt várja meg, és csak UTÁNA tölt —
// különben a fetch megelőzné a feltöltéseket, és a válaszból hiányoznának.
let guestUploadInFlight: Promise<void> | null = null;

const uploadGuestWishlist = (dispatch: ReturnType<typeof useAppDispatch>): Promise<void> => {
  if (!guestUploadInFlight) {
    guestUploadInFlight = (async () => {
      // A tételeket a feltöltés ELŐTT kivesszük a tárolóból.
      const pending = readGuestWishlist();
      if (pending.length > 0) writeGuestWishlist([]);

      const failed: WishlistProduct[] = [];
      for (const product of pending) {
        try {
          await dispatch(addToWishlistAsync(product.id)).unwrap();
        } catch (error) {
          console.error('Kívánságlista-tétel szinkronizálása sikertelen:', error);
          failed.push(product);
        }
      }
      // A sikertelenek visszakerülnek, a következő bejelentkezéskor újrapróbáljuk.
      if (failed.length > 0) writeGuestWishlist(failed);
    })().finally(() => {
      guestUploadInFlight = null;
    });
  }
  return guestUploadInFlight;
};

/**
 * Egyszeri szinkronizáció — CSAK EGY helyen (App) hívandó.
 * Bejelentkezéskor feltölti a vendég-tételeket, majd egyszer betölti a
 * szerveres listát; kijelentkezéskor kiüríti a Redux-állapotot.
 */
export const useWishlistSync = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(clearWishlistState());
      return;
    }

    let cancelled = false;
    const sync = async () => {
      await uploadGuestWishlist(dispatch);
      // A feltöltések UTÁN töltünk, így a válasz már tartalmazza őket
      // (korábban a korai fetch felülírhatta a frissen hozzáadott tételeket).
      if (!cancelled) dispatch(fetchWishlist());
    };
    sync();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, dispatch]);
};

/**
 * Kívánságlista-kezelés (vendégnek localStorage, bejelentkezve API/Redux).
 * Reaktív: a visszaadott lista / isInWishlist / számláló automatikusan frissül
 * bármely változásra, nincs szükség kézi eseményfigyelésre.
 */
export const useWishlist = () => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const { items: reduxWishlist, loading } = useSelector((state: RootState) => state.wishlist);
  const guestRaw = useSyncExternalStore(subscribeGuestWishlist, getGuestWishlistRaw);

  const wishlist = useMemo<WishlistProduct[]>(() => {
    if (isAuthenticated) return reduxWishlist.map(toWishlistProduct);
    return parseGuestWishlist(guestRaw);
  }, [isAuthenticated, reduxWishlist, guestRaw]);

  const ids = useMemo(() => new Set(wishlist.map((p) => p.id)), [wishlist]);

  const addToWishlist = useCallback(
    async (product: WishlistProduct): Promise<boolean> => {
      if (isAuthenticated) {
        try {
          await dispatch(addToWishlistAsync(product.id)).unwrap();
          return true;
        } catch (error) {
          console.error('Failed to add product to wishlist:', error);
          return false;
        }
      }
      try {
        const current = readGuestWishlist();
        if (current.some((p) => p.id === product.id)) return false;
        writeGuestWishlist([{ ...product, addedAt: new Date().toISOString() }, ...current]);
        return true;
      } catch (error) {
        console.error('Failed to add product to wishlist:', error);
        return false;
      }
    },
    [isAuthenticated, dispatch],
  );

  const removeFromWishlist = useCallback(
    async (productId: number): Promise<boolean> => {
      if (isAuthenticated) {
        try {
          await dispatch(removeFromWishlistAsync(productId)).unwrap();
          return true;
        } catch (error) {
          console.error('Failed to remove product from wishlist:', error);
          return false;
        }
      }
      try {
        writeGuestWishlist(readGuestWishlist().filter((p) => p.id !== productId));
        return true;
      } catch (error) {
        console.error('Failed to remove product from wishlist:', error);
        return false;
      }
    },
    [isAuthenticated, dispatch],
  );

  const clearWishlist = useCallback(async (): Promise<boolean> => {
    if (isAuthenticated) {
      try {
        for (const item of reduxWishlist) {
          await dispatch(removeFromWishlistAsync(item.id)).unwrap();
        }
        return true;
      } catch (error) {
        console.error('Failed to clear wishlist:', error);
        return false;
      }
    }
    try {
      writeGuestWishlist([]);
      return true;
    } catch (error) {
      console.error('Failed to clear wishlist:', error);
      return false;
    }
  }, [isAuthenticated, dispatch, reduxWishlist]);

  const getWishlist = useCallback(() => wishlist, [wishlist]);
  const isInWishlist = useCallback((productId: number) => ids.has(productId), [ids]);
  const getWishlistCount = useCallback(() => wishlist.length, [wishlist]);

  const toggleWishlist = useCallback(
    async (product: WishlistProduct): Promise<boolean> => {
      if (ids.has(product.id)) {
        await removeFromWishlist(product.id);
        return false;
      }
      await addToWishlist(product);
      return true;
    },
    [ids, addToWishlist, removeFromWishlist],
  );

  return {
    wishlist,
    addToWishlist,
    removeFromWishlist,
    getWishlist,
    clearWishlist,
    isInWishlist,
    getWishlistCount,
    toggleWishlist,
    loading,
  };
};
